import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import ApplyButton from './ApplyButton';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
  index: number;
}
const FAQItem = ({
  question,
  answer,
  isOpen,
  toggleOpen,
  index
}: FAQItemProps) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="border-b border-gray-200" data-id="element-46">
      <button onClick={toggleOpen} className="flex justify-between items-center w-full py-5 sm:py-6 px-1 sm:px-2 text-left focus:outline-none" data-id="element-47">
        <h4 className="text-gray-900 font-medium text-base sm:text-xl typekit-heading pr-4" data-id="element-48">
          {question}
        </h4>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-49">
          <ChevronDownIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[#ffdf07]" data-id="element-50" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-51">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-52">
            <div className="pb-5 sm:pb-6 px-1 sm:px-2 text-gray-600 libre-caslon-text-regular" data-id="element-53">
              <p className="leading-relaxed text-base sm:text-lg" data-id="element-54">{answer}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
};
interface FAQSectionProps {
  scrollToCalendar?: () => void;
}
const FAQSection = ({
  scrollToCalendar
}: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const whatThisIsNot = ['Not a replacement for your CPA or tax prep', "Not generic business consulting from people who don't understand healthcare", 'Not a software switch or EMR migration', 'Not an open‑ended consulting retainer', 'Not a done‑for‑you marketing or HR solution', 'Not a turnaround program for clinics in survival mode'];
  const faqItems = [{
    question: 'What does it cost?',
    answer: "Pricing depends on your clinic's size and complexity. We'll discuss on the strategy call."
  }, {
    question: 'Is there a guarantee?',
    answer: "Yes. If we don't identify opportunities worth at least 5× your fee, you don't pay."
  }, {
    question: 'What if I want to continue working together?',
    answer: '100% of your Sprint fee credits toward deeper engagement if you continue within 30 days.'
  }, {
    question: 'How much capacity do you have?',
    answer: 'Limited Sprints per month. Once booked, next openings roll to the following month.'
  }, {
    question: 'How much of my time will this take?',
    answer: 'About 3 hours for the founder, under 6 hours total for your team.'
  }, {
    question: "Can't my CPA do this?",
    answer: 'Your CPA optimizes for taxes. We analyze your numbers the way buyers and lenders do.'
  }];
  return <section id="faq" className="w-full bg-white py-12 sm:py-20 px-3 sm:px-4" data-id="element-55">
      <div className="max-w-3xl mx-auto" data-id="element-56">
        {/* What This Is Not Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} viewport={{
        once: true
      }} className="mb-12 sm:mb-20" data-id="element-57">
          <h2 className="text-gray-900 text-2xl sm:text-3xl md:text-4xl mb-6 sm:mb-8 typekit-heading text-center" data-id="element-58">
            What This Is Not
          </h2>
          <div className="bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-lg p-6 sm:p-8 shadow-sm" data-id="element-59">
            <ul className="space-y-4 sm:space-y-5" data-id="element-60">
              {whatThisIsNot.map((item, index) => <li key={index} className="flex items-start" data-id="element-61">
                  <XIcon className="text-red-500 mr-3 sm:mr-4 mt-1 flex-shrink-0" size={20} data-id="element-62" />
                  <span className="text-gray-600 libre-caslon-text-regular text-base sm:text-lg" data-id="element-63">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.div>

        {/* Investment Section - Condensed */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} viewport={{
        once: true
      }} className="mb-12 sm:mb-20" data-id="element-64">
          <h2 className="text-gray-900 text-2xl sm:text-3xl md:text-4xl mb-6 sm:mb-8 typekit-heading text-center" data-id="element-65">
            Investment & Guarantee
          </h2>
          <div className="bg-gradient-to-b from-amber-50 to-white border border-[#ffdf07]/30 rounded-lg p-6 sm:p-8 shadow-sm" data-id="element-66">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center" data-id="element-67">
              <div data-id="element-68">
                <p className="text-[#b8860b] font-semibold text-base sm:text-lg mb-2" data-id="element-69">
                  Fee
                </p>
                <p className="text-gray-700 libre-caslon-text-regular text-base sm:text-lg" data-id="element-70">
                  Custom based on clinic size
                </p>
              </div>
              <div data-id="element-71">
                <p className="text-[#b8860b] font-semibold text-base sm:text-lg mb-2" data-id="element-72">
                  Guarantee
                </p>
                <p className="text-gray-700 libre-caslon-text-regular text-base sm:text-lg" data-id="element-73">
                  5× ROI or you don't pay
                </p>
              </div>
              <div data-id="element-74">
                <p className="text-[#b8860b] font-semibold text-base sm:text-lg mb-2" data-id="element-75">
                  Credit
                </p>
                <p className="text-gray-700 libre-caslon-text-regular text-base sm:text-lg" data-id="element-76">
                  100% applies to next phase
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} viewport={{
        once: true
      }} className="text-center mb-8 sm:mb-12" data-id="element-77">
          <h2 className="text-gray-900 text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6 typekit-heading" data-id="element-78">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="mt-6 sm:mt-8" data-id="element-79">
          {faqItems.map((item, index) => <FAQItem key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} toggleOpen={() => toggleFAQ(index)} index={index} data-id="element-80" />)}
        </div>

        <div className="mt-10 sm:mt-14" data-id="element-81">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-82">
              <div className="relative" data-id="element-83">
                <div className="button-border-light" data-id="element-84"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 sm:py-4 px-8 sm:px-12 rounded-md text-base sm:text-lg shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-85">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-86" />}
        </div>
      </div>
    </section>;
};
export default FAQSection;